// Frontend money helpers. The wire form of an amount is the backend's
// "simplified dinero": `{ amount: "123.45", currency: "NOK" }` (decimal string +
// ISO code). Here we rehydrate that into a full dinero.js object for exact,
// currency-aware math, and serialize back when sending.
//
// The minor-unit exponent comes from `currencyExponent` (generated from the
// backend's single source of truth in `backend/src/types/money.rs`), so the
// scale we build dinero with is exactly the precision the backend rounded to —
// the two ends can never disagree about øre vs cents.

import {
  dinero,
  toDecimal,
  toSnapshot,
  add as dineroAdd,
  subtract as dineroSubtract,
  lessThan as dineroLessThan,
  greaterThan as dineroGreaterThan,
  isNegative as dineroIsNegative,
  isZero as dineroIsZero,
  type Dinero,
} from "dinero.js";
import { currencyExponent, type Currency, type Money } from "../api/types";

/// The home currency stamped on NOK amounts — mirrors `home_currency()` in the
/// backend (`CurrencyFormat` is single-valued today).
export const HOME_CURRENCY = "NOK" as Currency;

const dineroCurrency = (code: string) => ({
  code,
  base: 10,
  exponent: currencyExponent(code),
});

/// Parse a decimal string (`"-123.45"`) into an integer count of minor units,
/// exactly (no float). Padding/truncating the fractional part to `exponent`.
const decimalStringToMinorUnits = (value: string, exponent: number): number => {
  const negative = value.trim().startsWith("-");
  const abs = value.trim().replace(/^[-+]/, "");
  const [intPart = "0", fracPart = ""] = abs.split(".");
  const frac = (fracPart + "0".repeat(exponent)).slice(0, exponent);
  const minor = Number(`${intPart}${frac}`);
  return negative ? -minor : minor;
};

/// Format integer minor units back into a canonical decimal string with exactly
/// `exponent` fractional digits (`12340, 2 → "123.40"`; `124, 0 → "124"`).
const minorUnitsToDecimalString = (minor: number, exponent: number): string => {
  const negative = minor < 0;
  const digits = Math.abs(minor)
    .toString()
    .padStart(exponent + 1, "0");
  const body =
    exponent === 0
      ? digits
      : `${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`;
  return negative ? `-${body}` : body;
};

/// Rehydrate a wire `Money` into a dinero object for math/formatting.
export const toDinero = (money: Money): Dinero<number> =>
  dinero({
    amount: decimalStringToMinorUnits(
      money.amount,
      currencyExponent(money.currency),
    ),
    currency: dineroCurrency(money.currency),
  });

/// Serialize a dinero object back into the wire `Money` shape.
export const fromDinero = (value: Dinero<number>): Money => {
  const amount = toDecimal(value);
  const { currency } = toSnapshot(value);
  return { amount, currency: currency.code as Currency };
};

/// Build a `Money` from a user/decimal value (string or number) and a currency,
/// normalizing to the currency's minor-unit precision. Use for form input and
/// constructing request payloads.
export const makeMoney = (
  value: string | number,
  currency: string = HOME_CURRENCY,
): Money => {
  const exponent = currencyExponent(currency);
  const minor = decimalStringToMinorUnits(String(value), exponent);
  return {
    amount: minorUnitsToDecimalString(minor, exponent),
    currency: currency as Currency,
  };
};

/// Zero in the given currency (home by default).
export const zeroMoney = (currency: string = HOME_CURRENCY): Money =>
  makeMoney(0, currency);

/// The numeric value, for `Intl.NumberFormat` display. Safe because the amount
/// is already at minor-unit precision; never use the result for further math.
export const moneyToNumber = (money: Money): number => Number(money.amount);

/// Same value and currency. Wire amounts are canonical (minor-unit precision,
/// see `makeMoney`), so the decimal strings compare directly. Either side may
/// be null so optional amounts (`foreignAmount`) compare without unwrapping.
export const sameMoney = (a: Money | null, b: Money | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.amount === b.amount &&
    a.currency === b.currency);

export const isNegative = (money: Money): boolean =>
  dineroIsNegative(toDinero(money));

export const isZero = (money: Money): boolean => dineroIsZero(toDinero(money));

export const lessThan = (a: Money, b: Money): boolean =>
  dineroLessThan(toDinero(a), toDinero(b));

export const greaterThan = (a: Money, b: Money): boolean =>
  dineroGreaterThan(toDinero(a), toDinero(b));

export const addMoney = (a: Money, b: Money): Money =>
  fromDinero(dineroAdd(toDinero(a), toDinero(b)));

export const subtractMoney = (a: Money, b: Money): Money =>
  fromDinero(dineroSubtract(toDinero(a), toDinero(b)));
